// providers/AlarmProvider.jsx

import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import AlarmDialog from "../components/AlarmDialog";
import { ackAlarm  } from "../components/axios/alarmAxios";
import { useAlarmQuery } from "../hooks/queries/useAlarmQuery";
import alarmMp3 from "../assets/alert.mp3";
import usePermission from "hooks/usePermission";
import { PERMISSIONS } from "config/permissions";

const AlarmContext = createContext();
const ALLOWED_PATH_PREFIXES = [
  "/dashboard",
];

export const AlarmProvider = ({ children }) => {
  const location = useLocation();
  const queryClient = useQueryClient();
  const { hasPermission } = usePermission();
  const alarmReadPermission = hasPermission(
    PERMISSIONS.DASHBOARD_ALRAM_READ
  );
  const alarmReleasePermission = hasPermission(
    PERMISSIONS.DASHBOARD_ALRAM_RELEASE
  );
  
  const isAlarmEnabledPage = useMemo(() => {
    const result =
    ALLOWED_PATH_PREFIXES.some((path) => {
      return location.pathname.startsWith(
        path
      );
    });

    return result;
  }, [location.pathname]);

  const { loginId } = useSelector(state => state.user);
  const isLoggedIn = loginId !== 'NONE';

  const [alarmQueue, setAlarmQueue] = useState([]);
  const [isSoundEnabled, setIsSoundEnabled] = useState(true);

  const audioRef = useRef(null);

  const { data } = useAlarmQuery({
    enabled:
      isLoggedIn &&
      isAlarmEnabledPage &&
      alarmReadPermission,
  });

  useEffect(() => {
    audioRef.current = new Audio(alarmMp3);

    audioRef.current.volume = 1;

    // 반복 재생
    audioRef.current.loop = true;

    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
        audioRef.current = null;
      }
    };
  }, []);

  // mute 상태 반영
  useEffect(() => {
    if (!audioRef.current) return;

    // 권한이 없거나 소리가 꺼진 경우
    if (!alarmReleasePermission || !isSoundEnabled) {
      audioRef.current.pause();

      return;
    }

    // queue 존재
    if (alarmQueue.length > 0) {
      audioRef.current
        .play()
        .catch(() => {});
    }
    // queue 없음
    else {
      audioRef.current.pause();

      audioRef.current.currentTime = 0;
    }
  }, [alarmQueue, isSoundEnabled, alarmReleasePermission]);

  // chrome autoplay unlock
  useEffect(() => {
    const unlockAudio = () => {
      if (!audioRef.current) return;

      audioRef.current
        .play()
        .then(() => {
          audioRef.current.pause();

          audioRef.current.currentTime = 0;
        })
        .catch(() => {});

      window.removeEventListener(
        "click",
        unlockAudio
      );
    };

    window.addEventListener(
      "click",
      unlockAudio
    );

    return () => {
      window.removeEventListener(
        "click",
        unlockAudio
      );
    };
  }, []);

  useEffect(() => {
    if (!isAlarmEnabledPage || !alarmReadPermission) {
      setAlarmQueue([]);
      return;
    }
    
    const serverAlarms = data?.categories || [];

    setAlarmQueue((prev) => {
      const currentAlarm = prev[0];

      const currentStillExists =
        currentAlarm &&
        serverAlarms.some(
          (alarm) => alarm.id === currentAlarm.id
        );

      const nextQueue = [];

      if (currentStillExists) {
        nextQueue.push(
          serverAlarms.find(
            (alarm) => alarm.id === currentAlarm.id
          )
        );
      }

      serverAlarms.forEach((alarm) => {
        const exists = nextQueue.some(
          (item) => item.id === alarm.id
        );

        if (!exists) {
          nextQueue.push(alarm);
        }
      });

      return nextQueue;
    });
  }, [data, isAlarmEnabledPage, alarmReadPermission]);

   // 현재 표시 경보
  const currentAlarm = alarmQueue[0];

  // ACK mutation
  const ackMutation = useMutation({
    mutationFn: ackAlarm,
    onSuccess: (_, payload) => {
      setAlarmQueue((prev) =>
        prev.filter(
          (alarm) =>
            alarm.id !== payload.id
        )
      );

      queryClient.invalidateQueries({
        queryKey: ["alarm-list"],
      });
    },

    onError: (err) => {
      console.error(err);

      alert(
        "Alarm confirmation processing failed"
      );
    },
  });

  // 확인 버튼(해당 경보를 확인 후에는 intervalMinutes 시간 후에도 다시 뜨지 않음)
  const handleOneceConfirm = async () => {
    if (!currentAlarm || !alarmReleasePermission) return;

    ackMutation.mutate({
      id: currentAlarm.id,
      alarmYn: "N",
    });
  };

  // 확인 버튼
  const handleConfirm = async () => {
    if (!currentAlarm || !alarmReleasePermission) return;

    ackMutation.mutate({
      id: currentAlarm.id,
      alarmYn: "Y",
    });
  };

  
  return (
    <AlarmContext.Provider
      value={{
        alarmQueue,
        currentAlarm,
        handleConfirm,
        handleOneceConfirm,
        isSoundEnabled,
        setIsSoundEnabled,
        isAckLoading: ackMutation.isPending,
        alarmReleasePermission,
      }}
    >
      {children}

      {isAlarmEnabledPage && alarmReadPermission && <AlarmDialog />}
    </AlarmContext.Provider>
  );
};

export const useAlarm = () => {
  return useContext(AlarmContext);
};